import React, { useEffect, useState } from 'react'
import SubHeader from '../Shared/Display/SubHeader'
import DateTimeFilter from '../Shared/Buttons/DateTimeFilter'
import CustomFilter from '../Shared/Buttons/CustomFilter'
import UserTable from '../Shared/Data/UserTable'
import { useDispatch, useSelector } from 'react-redux'
import { fetchUsers } from '../Redux/Actions/Content'
import { fetchOverview } from '../Redux/Actions/Overview'
import { time, numberedDate, dateDelta } from '../Shared/timeutils'
import { generateUserReport } from '../Redux/Actions/GenerateReport'
import { componentCreated, updateDateFilter, updateGroupFilter } from '../Redux/Actions/DateFilter'
import FilterBy from '../Shared/Filters/FilterBy'
import InfoBlock from '../Shared/Data/UserActivityItems/InfoBlock'
import DynamicInfoBlock from '../Shared/Data/UserActivityItems/DynamicInfoBlock'
import { useLocation, useNavigate } from 'react-router'
import {
  composeFilterString,
  getDateFilter,
  getGroupFilter,
  processDate,
  processGroup,
  formatStartEndDates
} from '../utils/analytics-utils'
import { addQuery } from '../utils/router-utils'

/** @const {number} pageTotals */
const pageTotals = 10

const processUserData = (data) => {
  return data.map((d) => {
    return { ...d, totalTime: (d.totalLogins * d.avgDurationInApp) }
  })
}

function Individual () {
  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [page, setPage] = useState(1)
  const [sort, setSort] = useState('latestActivity DESC')

  const id = useSelector(state => state.account.ID)
  const userActivity = useSelector(state => processUserData(state.content.users))
  const userLength = useSelector(state => state.content.usersLength)
  const overview = useSelector(state => state.overview)
  const userAnalyticsEnabled = useSelector(state => state.account.userAnalyticsEnabled)
  const redirect = useSelector(state => state.account.redirect)
  const status = useSelector(state => state.generateReport.status)
  const dateFilterStore = useSelector(state => state.dateFilter)
  const name = 'Individual'

  const totalLicenses = overview.totalLicenses
  const totalMembers = overview.totalMembers
  const totalRegisteredUsers = overview.absoluteTotalReg
  const activeUsers = overview.activeUsers
  const inactiveUsers = Math.max(0, totalRegisteredUsers - activeUsers)

  let dateFilterFromStore = getDateFilter(dateFilterStore, name)
  dateFilterFromStore = dateFilterFromStore ? dateFilterFromStore.dateFilter || 'Week' : 'Week'
  const [dateFilter, setDateFilter] = useState(dateFilterFromStore)

  const groupFilterFromStore = getGroupFilter(dateFilterStore, name) || ''
  const [groupFilter, setGroupFilter] = useState(groupFilterFromStore)
  const dateInterval = dateFilter ? dateFilter.dateInterval || null : null

  useEffect(() => {
    dispatch(componentCreated(name))
  }, [])

  useEffect(() => {
    update(processDate(dateFilter), processGroup(groupFilter))
  }, [page, sort])

  useEffect(() => {
    newQuery(location.search.substring(1).split('&'))
  }, [])

  const newQuery = (query) => {
    const qobj = {}
    let newDateFilter
    query.forEach((q) => {
      qobj[q.split('=')[0]] = q.split('=')[1]
    })
    if (dateFilter === 'Custom' && dateInterval) {
      newDateFilter = dateInterval
    } else {
      newDateFilter = dateFilter
    }
    update(processDate(newDateFilter), processGroup(groupFilter), id)
  }

  const update = (date, group, newID) => {
    const formattedDate = formatStartEndDates(date)
    const ID = newID || id
    dispatch(fetchUsers(ID, formattedDate.start, formattedDate.end, {
      limit: pageTotals,
      offset: (page - 1) * pageTotals,
      group: group.groups,
      profile: group.profiles,
      sort,
      appType: group.alt
    }))
    overview.status === 'Loading' || dispatch(fetchOverview(ID, formattedDate.start, formattedDate.end, {
      group: group.groups,
      profile: group.profiles
    }))
  }

  const changeDateFilter = (newFilter, isCustom) => {
    if (isCustom) {
      dispatch(updateDateFilter(name, 'Custom', newFilter))
    } else {
      dispatch(updateDateFilter(name, newFilter))
    }
    const f = `${composeFilterString(groupFilter.profiles, groupFilter.groups)}_${groupFilter.alt || ''}`
    addQuery({ date: newFilter, filter: f }, location, navigate)
    setDateFilter(newFilter)
    if (newFilter !== 'Custom') {
      update(processDate(newFilter), processGroup(groupFilter))
    }
  }

  const changeGroupFilter = (newFilter) => {
    let newDateFilter
    if (dateFilter === 'Custom' && dateInterval) {
      newDateFilter = dateInterval
    } else {
      newDateFilter = dateFilter
    }

    dispatch(updateGroupFilter(name, newFilter))
    const f = `${composeFilterString(newFilter.profiles, newFilter.groups)}_${newFilter.alt || ''}`
    addQuery({ date: newDateFilter, filter: f }, location, navigate)
    setGroupFilter(newFilter)
    update(processDate(newDateFilter), processGroup(newFilter))
  }

  if (userAnalyticsEnabled) {
    return (
      <div id='individualActivity'>
        <div className='toolbar'>
          <SubHeader type='Individual' />
          <DateTimeFilter filterBy={changeDateFilter} currentFilter={dateFilter} />
          <div className='reportButtons'>
            {/* <FavoriteButton /> */}
          </div>
        </div>
        {(dateFilter !== 'Day' && dateFilter !== 'Week' && dateFilter !== 'Month')
          ? <CustomFilter
          filterBy={changeDateFilter} currentFilter={dateFilter} />
          : ''}

        <FilterBy changeFilter={changeGroupFilter} currentFilter={processGroup(groupFilter)} showProfiles showGroups type='' />

        <div className='BlockContainer'>
          <InfoBlock
            title='New Registrations'
            usePercent={false}
            showTooltip={false}
            value={overview.totalRegisteredUsers} delta={overview.registrationsChange}
            sinceText={dateDelta(dateFilter)} />
          <InfoBlock
            title='Average Duration On Mobile App'
            usePercent
            showTooltip
            value={time(overview.avgMobileAppDuration)} delta={overview.avgMobileAppDurationChangePct}
            sinceText={dateDelta(dateFilter)} />
          <InfoBlock
            title='Average Duration On Web App'
            usePercent
            showTooltip
            value={time(overview.avgWebAppDuration)} delta={overview.avgWebAppDurationChangePct}
            sinceText={dateDelta(dateFilter)} />
        </div>

        <div className='BlockContainer'>
          <DynamicInfoBlock
            value={totalMembers}
            title='Total Licensed Users'
            maxThreshold={totalLicenses}
            minThreshold={totalLicenses * 0.6}
            invert={false}
            users />
          <DynamicInfoBlock
            value={totalRegisteredUsers}
            title='Total Users'
            maxThreshold={totalMembers}
            minThreshold={totalMembers * 0.6}
            invert={false}
            users />
          <DynamicInfoBlock
            value={inactiveUsers}
            title='Inactive Users'
            maxThreshold={totalRegisteredUsers}
            minThreshold={totalRegisteredUsers * 0.6}
            invert
            users={false} />
          <DynamicInfoBlock
            value={activeUsers}
            title='Active Users'
            maxThreshold={totalRegisteredUsers}
            minThreshold={totalRegisteredUsers * 0.6}
            invert={false}
            users={false} />
        </div>

        <UserTable
          status={status}
          generateReportClick={() => {
            const dates = processDate(dateFilter)
            const group = processGroup(groupFilter)
            const options = { group: group.groups, profile: group.profiles, sort, appType: group.alt }
            dispatch(generateUserReport(id, dates.start.toISOString(), dates.end.toISOString(), options))
          }}
          groupFilter={processGroup(groupFilter)}
          data={userActivity}
          fields={[{ ref: 'name', title: 'Name' },
            { ref: 'avgDurationInApp', title: 'Average Duration In Mobile', toMap: (duration) => time(duration) },
            { ref: 'avgDurationInCMS', title: 'Average Duration In Web', toMap: (duration) => time(duration) },
            { ref: 'appProfiles', title: 'Profiles' },
            { ref: 'groups', title: 'Groups' },
            { ref: 'latestActivity', title: 'Latest Activity', toMap: (day) => numberedDate(day) }]}
          changeOffset={(i) => {
            setPage(i)
          }}
          toggleSort={(key) => {
            if (sort.split(' ')[0] === key && sort.split(' ')[1] === 'DESC') {
              setSort(key + ' ASC')
            } else {
              setSort(key + ' DESC')
            }
          }}
          sort={sort}
          page={page}
          totalUsers={Math.ceil(userLength / pageTotals)}
        />
      </div>
    )
  } else {
    return (
      <div className='AdministratorTable'>
        <div className='adminPage'>
          <p>Individual user analytics have not been turned on. To do so, go to Master Administrator Account Settings
            and enable Individual User Analytics</p>
        </div>
        <div className='adminPageContainer'><a className='buttonRedirect' href={redirect}>Go to Master Administrator
          Account Settings</a></div>
      </div>
    )
  }
}

export default Individual
